import { Tooltip } from "@material-ui/core";
import { navigate } from "@reach/router";
import { Formik } from "formik";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import { getErrorMessage, getFileName } from "../../../helpers/functions";
import { BulkUploadSchema } from "../../../helpers/validationSchema";
import useLoader from "../../../hooks/useLoader";
import AWSService from "../../../services/AWSService";
import CaseService from "../../../services/CaseService";
import PartyService from "../../../services/PartyService";
import ActionBar from "../../common/ActionBar";
import { PrimaryCTAButton } from "../../common/Buttons";
import FormField from "../../common/FormField/FormField";
import { CustomFileUpload } from "../../common/FormInputs";
import { HyperLink } from "../../common/styled-components";
import _ from "lodash";
import BulkCaseTypeRadioButton from "./BulkCaseTypeRadioButton";
import SystemService from "../../../services/SystemService";
import ContractTable from "./ContractTable";
import useUser from "../../../hooks/useUser";
import AlertDialog from "./AlertWrapper";

/* eslint-disable jsx-a11y/anchor-is-valid */

export default function CasesBulkCreate(caseType) {
  const resolutionType = caseType?.location?.search;
  const resolutionKind = resolutionType.split("=").pop();
  const [data, setData] = useState("");
  const breadcrumbs = [
    "My Cases",
    "Bulk Upload",
    _.capitalize(data?.resolutionKind),
  ];
  const [menuItems, setMenuItems] = useState([]);
  const [agents, setAgents] = useState([]);
  const [success, setSuccess] = useState(false);
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [systemConfig, setSystemConfig] = useState();
  const [modal, setModal] = useState({ state: false });
  const { userObject } = useUser();
  const [dialogData, setDialogData] = useState({});
  const [open, setOpen] = useState(false);

  const THREE_MINUTES = 3 * 60 * 1000;

  const parseDateTime = (dateTime) => {
    if (!dateTime) return null;

    const [date, time] = dateTime.split(" ");
    const [year, month, day] = date.split("-");
    const [hour, minute, second] = time.split(":");

    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
    ).getTime();
  };

  const getIsExpired = (time) => {
    const createdTime = parseDateTime(time);

    if (!createdTime) return false;

    return Date.now() - createdTime > THREE_MINUTES;
  };

  const fetchBulkUpload = async () => {
    try {
      const res = await CaseService.bulkUpload("?page=1&perPage=10");
      const apiData = res?.data?.[0];

      if (!apiData) return null;

      const payload = {
        time: apiData?.created_at,
        revert: !!apiData?.updatedFileWithCaseId,
        batchId: apiData?.id,
        comments: apiData?.comments,
      };
      return payload;
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    }
  };

  useEffect(() => {
    async function init() {
      setLoader({ state: true, message: "Please wait. Loading..." });
      try {
        await fetchBulkUpload();
        const [configRes, partyRes] = await Promise.all([
          SystemService.getConfigAuth(),
          PartyService.getParties("?perPage=1000&status=active&allAgents=true"),
        ]);

        setSystemConfig(configRes.systemConfig);

        if (partyRes?.data?.length) {
          setMenuItems(
            partyRes.data.map(({ name, id, partyCode }) => ({
              label: name,
              value: id,
              partyCode,
            })),
          );

          setAgents(partyRes.data.flatMap(({ agents }) => agents));
        } else {
          enqueueSnackbar(
            "You need to create a Party inorder to create a case",
            { variant: "error" },
          );
        }
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error), { variant: "error" });
      } finally {
        setLoader({ state: false });
      }
    }
    init(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onBreadcrumbClick = (selected) => {
    switch (_.snakeCase(selected)) {
      case "bulk_upload":
        navigate(`/dashboard/cases?caseType=${data?.resolutionKind}`);
        break;
      case "my_cases":
        navigate(`/dashboard/cases?caseType=${data?.resolutionKind}`);
        break;
      default:
        break;
    }
  };

  async function s3Upload(file, field, setFieldValue, setFieldError) {
    if (file) {
      try {
        const types = [
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "application/vnd.ms-excel",
          "text/csv",
        ];
        setLoader({ state: true, message: `Uploading file ${file.name}` });
        const errorMessage = "Invalid File Type";
        if (!types.includes(file?.type)) throw errorMessage;
        const payload = {
          key: file.name,
        };
        const s3Response = await AWSService.getS3URL(payload);
        if (s3Response.url) {
          const res = await AWSService.uploadToS3(s3Response.url, file);
          setFieldValue(field, res);
        }
      } catch (error) {
        const message = getErrorMessage(error);
        enqueueSnackbar(message, { variant: "error" });
      } finally {
        setLoader({ state: false });
      }
    }
  }

  async function onFormSubmit(values, { resetForm }) {
    try {
      const latest = await fetchBulkUpload();

      if (!latest) return;

      const isReverted = latest?.revert;
      const comments = latest?.comments;
      const isExpired = getIsExpired(latest?.time);

      if (!isReverted && isExpired && comments !== "reverted") {
        setOpen(true);
        setDialogData({
          primaryBtnText: "Cancel",
          secondaryBtnText: "Rollback & Retry",
          clickSecondarybtn: () =>
            revertBulkCaseUpload(latest?.batchId, resetForm),
          clickPrimaryBtn: () => setOpen(false),
          onDialogClose: () => setOpen(false),
          desc: `The previous action may not have completed successfully during case ID generation. Kindly review the details, perform a rollback, and proceed again with the correct input.`,
          heading: "Action Required: Case ID Generation Issue Detected",
          status: true,
          descriptionTextStyle: {
            textAlign: "left",
            fontFamily: theme.fonts.primaryFontSemiBold,
          },
        });
        return;
      }

      if (!isReverted && !isExpired && comments !== "reverted") {
        enqueueSnackbar(
          "Your request is being processed. Case ID generation may take up to 3 minutes",
          { variant: "info" },
        );
        return;
      }

      setLoader({
        state: true,
        message: `Uploading ${getFileName(values.url)}...`,
      });
      const selectedItem = menuItems.find(
        (item) => item?.value === values?.partyId,
      );

      const partyCode = selectedItem?.partyCode || "";
      const checkerId = values?.checkerId || "";

      const payload = {
        url: values.url,
        ...(checkerId && { checkerId }),
        ...(partyCode && { partyCode }),
      };

      let urlKind = "bulkCaseUploadMediation";

      if (data.resolutionKind === "negotiation") {
        urlKind = "bulkCaseUpload";
      } else if (data.resolutionKind === "arbitration") {
        urlKind = "bulkCaseUploadArbitration";
      }
      const response = await CaseService.caseBulkCreate(
        payload,
        values.partyId,
        urlKind,
      );
      if (response?.message === "Document uploaded Successfully") {
        setSuccess(true);
        await fetchBulkUpload();

        enqueueSnackbar(
          "Your request is being processed. Case ID generation may take up to 3 minutes",
          { variant: "success" },
        );
      } else {
        enqueueSnackbar(response?.message, { variant: "error" });
        setModal({
          state: true,
          metaData: response?.contractNo.map((item) => ({
            contractNo: item,
            caseId: "-",
            createdAt: "-",
          })),
        });
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  }

  async function revertBulkCaseUpload(id, resetForm) {
    try {
      setLoader({ state: true, message: `rollback...` });
      const response = await CaseService.revertBulkCaseUpload({ batchId: id });
      if (response) {
        enqueueSnackbar(response?.message, {
          variant: "success",
        });
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoader({ state: false });
      setOpen(false);
      resetForm();
    }
  }

  /*

  const isReverted = createdAt?.revert;
  const isExpired = getIsExpired(createdAt?.time);

  const actions = [
    {
      text: "Rollback Cases",
      props: {
        onClick: () => revertBulkCaseUpload(createdAt?.batchId),
      },
      hidden: isReverted || !isExpired,
    },
  ];

  */

  return (
    <Container>
      <ActionBar {...{ breadcrumbs, onBreadcrumbClick }} />
      <Formik
        initialValues={{
          partyId: "",
          url: "",
          resolutionKind: resolutionKind || "negotiation",
          checkerId: "",
          userId: userObject?.isRequiredChecker || "",
        }}
        validationSchema={BulkUploadSchema}
        enableReinitialize
        onSubmit={onFormSubmit}
        validateOnBlur
        validateOnChange
      >
        {({
          values,
          handleChange,
          errors,
          handleSubmit,
          touched,
          handleBlur,
          setFieldValue,
          setFieldError,
          resetForm,
        }) => (
          <FormContainer onSubmit={handleSubmit}>
            <Heading>
              Bulk Case Upload - {_.capitalize(data?.resolutionKind)}
              {success && " Success"}
            </Heading>
            {success ? (
              ""
            ) : (
              <BulkCaseTypeRadioButton
                values={values}
                handleChange={(e, type) => {
                  handleChange(e);
                }}
                resolutionKind={resolutionKind}
              />
            )}
            {setData(values)}
            <Subheading>
              {!success ? (
                <>
                  <div>
                    Bulk cases have to be uploaded in a specified template
                    format.
                  </div>
                  <div>
                    If you find it difficult to upload the file from your
                    mobile, please use a desktop/laptop to upload.
                  </div>
                  {data?.resolutionKind === "negotiation" ? (
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={systemConfig?.bulkUploadNegTemplateUrl}
                    >
                      Please download the template.
                    </a>
                  ) : data?.resolutionKind === "mediation" ? (
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={systemConfig?.bulkUploadMedTemplateUrl}
                    >
                      Please download the template.
                    </a>
                  ) : (
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={systemConfig?.bulkUploadArbTemplateUrl}
                    >
                      Please download the template.
                    </a>
                  )}
                </>
              ) : (
                `You can see all the bulk uploaded cases under MyCases with status as Drafts.`
              )}
            </Subheading>
            {!success && (
              <>
                <Button>
                  <FormField
                    white
                    input={{
                      type: "select",
                      name: "partyId",
                      label: "SELECT A CLAIMANT PARTY",
                      required: true,
                    }}
                    menuItems={menuItems}
                    {...{
                      values,
                      handleChange,
                      errors,
                      handleSubmit,
                      touched,
                      handleBlur,
                      setFieldValue,
                      showId: true,
                    }}
                  />
                  <HyperLink
                    style={{
                      marginTop: 10,
                      float: "right",
                      width: "auto",
                    }}
                    onClick={() => navigate("/dashboard/parties/create")}
                  >
                    Setup a Party
                  </HyperLink>
                </Button>
                <StyledLabel
                  style={{ width: "auto" }}
                  tooltip={"Case template for doing bulk upload"}
                >
                  Bulk Case xls/CSV file
                  <span className="required-star"> *</span>
                </StyledLabel>
                <Button>
                  <CustomFileUpload
                    key={values.url}
                    error={touched.url && errors.url}
                    file={{ name: getFileName(values.url) }}
                    fileUrl={values?.url}
                    setFile={(file) =>
                      s3Upload(file, "url", setFieldValue, setFieldError)
                    }
                    accept=".xlsx"
                    style={{ width: "100%" }}
                  />
                </Button>
                {values?.resolutionKind === "arbitration" && (
                  <Button
                    style={{
                      marginTop: 10,
                    }}
                  >
                    <FormField
                      white
                      input={{
                        type: "select",
                        name: "checkerId",
                        label: userObject?.isRequiredChecker
                          ? "Select a checker"
                          : "Select a checker (Optional)",

                        required: userObject?.isRequiredChecker ? true : false,
                      }}
                      menuItems={agents
                        .filter(({ partyId }) => partyId === values.partyId)
                        .map(({ email, userId, invitedById }) => ({
                          label: email,
                          value: userId,
                          userId: userId,
                          invitedById: invitedById,
                        }))}
                      {...{
                        values,
                        handleChange,
                        errors,
                        handleSubmit,
                        touched,
                        handleBlur,
                        setFieldValue,
                      }}
                    />
                  </Button>
                )}
                <Button>
                  <PrimaryCTAButton
                    onClick={handleSubmit}
                    style={{ marginTop: 25 }}
                  >
                    Submit
                  </PrimaryCTAButton>
                </Button>
              </>
            )}
          </FormContainer>
        )}
      </Formik>

      {modal?.state && (
        <ContractTable metaData={modal?.metaData} {...{ setModal }} />
      )}

      <AlertDialog
        isOpen={open}
        {...{ ...dialogData }}
        descriptionTextStyle={{
          textAlign: "left",
          fontFamily: theme.fonts.primaryFontSemiBold,
        }}
      />
    </Container>
  );
}

const Container = styled.div`
  border-radius: 10px;
  background-color: ${COLORS.PRIMARY_WHITE};
  margin: 40px 15px;
  padding-top: 1px;
  @media ${theme?.breakpoints?.sm_up} {
    margin: 0;
    padding-top: 0;
    border-radius: 0;
    background-color: transparent;
`;

const FormContainer = styled.form`
  padding: 20px 15px;
  @media ${theme?.breakpoints?.sm_up} {
    padding: 34px 53px;
  }
`;

const Button = styled.div`
  @media ${theme?.breakpoints?.sm_up} {
    width: 436px;
  }
`;

const Heading = styled.div`
  font-size: 18px;
  font-family: ${theme.fonts.primaryFontExtraBold};
  color: ${COLORS.COLOR_DARK};
  /* text-transform: uppercase; */
`;

const Subheading = styled.div`
  font-size: 12px;
  font-family: ${theme.fonts.primaryFontBold};
  margin-top: 23px;
  color: ${COLORS.COLOR_DARK};
  margin-bottom: 20px;
  & div {
    margin-bottom: 20px;
  }
  & a {
    color: ${COLORS.BTN_GREEN};
    text-decoration: underline;
    cursor: pointer;
  }
`;

const Flex = styled.div`
  display: flex;
  margin-top: 45px;
  margin-bottom: 6px;
  @media ${theme?.breakpoints?.sm_up} {
    margin-top: 19px;
  }
`;

const FormLabel = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 10px;
  line-height: 1.7;
  text-transform: uppercase;
  color: ${COLORS.INPUT_LABEL};
  margin-right: 8px;
`;

const TooltipIcon = styled.img`
  width: 17px;
  height: 17px;
  object-fit: contain;
  cursor: pointer;
`;

const StyledLabel = ({ tooltip, spacing, style, ...props }) => (
  <Flex spacing={spacing} style={style}>
    <FormLabel {...props} />
    {tooltip && (
      <Tooltip title={tooltip} placement="top">
        <TooltipIcon src={require("../../../assets/images/toolTip.png")} />
      </Tooltip>
    )}
  </Flex>
);
